import React from 'react';
import { Button, Grid, GridProps, Typography } from '@mui/material';
import { grey } from '@mui/material/colors';
import ErrorIcon from '@mui/icons-material/Error';
import ReplayIcon from '@mui/icons-material/Replay';

export interface IErrorStateProps extends Partial<GridProps> {
  /** Primary message displayed under the icon */
  message?: React.ReactNode;
  /** Description text displayed under primary message */
  description?: React.ReactNode;
  /** Override icon component */
  Icon?: typeof ErrorIcon;
  /** Set height to `100vh`. Default: `false` */
  fullScreen?: boolean;
  /** Basic inline presentation without padding. Default: `false` */
  basic?: boolean;
}

/**
 * Display an empty state message with sensible defaults.
 * By default, height is `100%`.
 * Override with props that are passed to the root MUI `Grid` component.
 */
export default function ErrorState({
  message = 'Nothing here',
  description,
  Icon = ErrorIcon,
  fullScreen = false,
  basic = false,
  ...props
}: IErrorStateProps) {

  if (basic)
    return (
      <Grid container alignItems="center" spacing={1} {...props} >
        <Grid item>
          <Icon sx={{ display: 'block' }} />
        </Grid>

        <Grid item>
          {message}
          {description && ': '}
          {description}
        </Grid>
      </Grid>
    );

  return (
    <Grid container
      direction="column" justifyContent="center"
      alignItems="center" textAlign="center"
      flex={1} width="100%" height="100%"
      {...props}
    >
      <Grid item maxWidth='25em'>
        <Icon sx={{ color: grey[500], fontSize: '3.5rem' }} />
        <Typography
          variant="h6" color="textSecondary" gutterBottom
          sx={{ textTransform: 'uppercase', mt: 1, letterSpacing: 1 }}
        >
          {message}
        </Typography>
        {description && (
          <Typography color="textSecondary" variant="body2">
            {description}
          </Typography>
        )}
        <Button variant='outlined'
          startIcon={<ReplayIcon fontSize='small' />}
          onClick={() => window.location.reload()}
          sx={{ mt: 2 }}
        >
          Retry
        </Button>
      </Grid>
    </Grid>
  );
}
